import { FC, useEffect, useMemo, useRef } from "react";

import {
  getNextSelectIndex,
  isSelectCloseKey,
  isSelectCommitKey,
  isSelectNavKey,
} from "web/utils/ui/selectKeyboard";

import { DISTANCE_DROPDOWN_ID } from "../const";
import classes from "./dropdown.scss";

interface IDropdownProps {
  options: {
    value: string;
    label: string;
  }[];
  onChange: (val: string) => void;
  value: string;
  isOpen: boolean;
  activeIndex?: number;
  onRequestActiveIndexChange?: (nextIndex: number) => void;
  onRequestSelect?: (index: number) => void;
  onRequestClose?: () => void;
}

const Dropdown: FC<IDropdownProps> = ({
  options,
  onChange,
  value,
  isOpen,
  activeIndex = -1,
  onRequestActiveIndexChange,
  onRequestSelect,
  onRequestClose,
}) => {
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const safeActiveIndex = useMemo(() => {
    if (activeIndex < 0) return -1;
    if (activeIndex >= options?.length) return options?.length - 1;
    return activeIndex;
  }, [activeIndex, options?.length]);

  useEffect(() => {
    if (!isOpen) return;
    if (safeActiveIndex < 0) return;
    const btn = optionRefs.current.get(
      `${DISTANCE_DROPDOWN_ID}-option-${safeActiveIndex}`
    );
    btn?.scrollIntoView({ block: "nearest" });
    btn?.focus();
  }, [isOpen, safeActiveIndex]);

  return (
    <ul
      className={classes.root}
      id={DISTANCE_DROPDOWN_ID}
      aria-hidden={!isOpen}
      role="listbox"
    >
      {options?.map((option, index) => {
        return (
          <li key={option.value} className={classes.option}>
            <button
              type="button"
              onClick={() => {
                onChange(option.value);
              }}
              data-t2={option.value}
              data-t1="distance_dropdown_button"
              tabIndex={isOpen ? 0 : -1}
              role="option"
              aria-selected={value === option.value}
              id={`${DISTANCE_DROPDOWN_ID}-option-${index}`}
              ref={(node) => {
                const key = `${DISTANCE_DROPDOWN_ID}-option-${index}`;
                if (!node) {
                  optionRefs.current.delete(key);
                  return;
                }
                optionRefs.current.set(key, node);
              }}
              onKeyDown={(e) => {
                if (!isOpen) return;
                const { key } = e;

                if (isSelectNavKey(key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  const nextIndex =
                    getNextSelectIndex({
                      key,
                      currentIndex: index,
                      count: options.length,
                    }) ?? index;
                  onRequestActiveIndexChange?.(nextIndex);
                  return;
                }

                if (isSelectCommitKey(key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  onRequestSelect?.(index);
                  return;
                }

                if (isSelectCloseKey(key)) {
                  e.preventDefault();
                  e.stopPropagation();
                  onRequestClose?.();
                }
              }}
            >
              {option.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default Dropdown;
